import { Component, input, Output, EventEmitter, inject, ChangeDetectionStrategy, signal, computed, effect } from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { RisrFormTransformer, RisrSchemaForm, AsyncWrapperComponent, RisrForm, RisrFormRenderer, ListApiSearchRequest } from "@fry-it/risr-core";
import { asyncWrapper } from "@fry-it/risr-core";
import { distinctUntilChanged, filter, map, Subject, switchMap, takeUntil } from "rxjs";
import { ReportStore } from "../../services/report-store";
import { Report } from "../../models/report";
import { ReportRunner } from "../../services/report-runner";
import { ReactiveFormsModule } from "@angular/forms";

@Component({
    selector: 'kz-report-widget-edit',
    templateUrl: './report-widget-edit.html',
    imports: [
        AsyncWrapperComponent,
        RisrFormRenderer,
        RisrFormRenderer,
        ReactiveFormsModule
    ],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class ReportWidgetEditComponent {
    config = input.required<any>()
    innerConfig = signal<any | undefined>({});

    // This needs to be the legacy output so that angularjs can bind to it
    @Output() configChange = new EventEmitter<any>();

    private readonly formTransformer = inject(RisrFormTransformer);
    private readonly reportStore = inject(ReportStore);
    private readonly reportRunner = inject(ReportRunner);
    private destroy$ = new Subject<void>();
    private reportFormDestroy$ = new Subject<void>();

    protected $report = signal<Report | undefined>(undefined);
    protected $reportForm = signal<RisrForm | undefined>(undefined);

    private mainConfig = signal<any | undefined>(undefined);
    private reportConfig = signal<any | undefined>(undefined);
    private configModel = computed(() => {
        return {
            ...this.mainConfig(),
            model: this.reportConfig()
        }
    });



    private reportSchema: RisrSchemaForm = {
        fields: [
            {
                name: 'report',
                label: 'Select a report',
                type: 'discrete',
                required: true,
                initialValue: '',
                options: {
                    categoriesSource: '/reports-manage/search',
                    categoriesSourceExtraData: { purpose: 'view' },
                    categoriesSourceRequestModifier: (request: ListApiSearchRequest) => {
                        const options = request.options || {};
                        options.modifier = 'stub_group';
                        request.options = options;
                        return request;
                    }
                }
            }
        ]
    }
    formWrapper = asyncWrapper<RisrForm>(async () => {
        const initialValue = {
            report: this.innerConfig().report,
        }
        return await this.formTransformer.fromRisrSchema(this.reportSchema, initialValue);
    });

    constructor() {
        effect(() => {
            this.configChange.emit(this.configModel());
        })
        this.formWrapper.ready.pipe(
            takeUntilDestroyed()
        ).subscribe((form) => {
            if (!form) {
                return;
            }
            this.setupForm(form);
        })

    }

    setupForm(form: RisrForm) {
        form.$value.pipe(
            takeUntil(this.destroy$)
        ).subscribe((value) => {
            this.mainConfig.set(value)
        })

        form.$value.pipe(
            takeUntil(this.destroy$),
            map((value: any) => value.report),
            filter((value: string) => !!value),
            distinctUntilChanged(),
            switchMap((value: string) => {
                return this.loadReportForm(value);
            })
        ).subscribe()
    }

    async ngOnInit() {
        // We want to create a copy so that we can emit to the modified config
        this.innerConfig.set(JSON.parse(JSON.stringify(this.config())));
        this.formWrapper.load();
        if (this.innerConfig().report) {
            this.loadReportForm(this.innerConfig().report);
        }
    }

    async loadReportForm(reportId: string): Promise<Report> {
        const report = await this.reportStore.getByGroup(reportId);
        const form = await this.reportRunner.getForm(report.report.filterForm, this.innerConfig().model || {});

        // Set the initial value
        this.reportConfig.set(form.value());

        this.$report.set(report);
        this.$reportForm.set(form);
        this.reportFormDestroy$.next();
        form.$value.pipe(
            takeUntil(this.destroy$),
            takeUntil(this.reportFormDestroy$)
        ).subscribe((value) => {
            this.reportConfig.set(value)
        })
        return report;
    }

    ngOnDestroy() {
        this.reportFormDestroy$.next();
        this.reportFormDestroy$.complete();
        this.destroy$.next();
        this.destroy$.complete();
    }


}
